import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
} from "@mui/material";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { styled } from "@mui/material/styles";

const BLUE_THEME = {
  primary: "#0d0651",
  primaryLight: "#1e1b72",
  primaryDark: "#050027",
  accent: "#2196F3",
  accentLight: "#42a5f5",
  accentDark: "#1976d2",
};

const API_BASE_URL = "http://localhost/backend/api";

const StyledCard = styled(Card)({
  maxWidth: 480,
  width: "100%",
  backdropFilter: "blur(20px)",
  background: `linear-gradient(145deg, ${BLUE_THEME.accent}08, rgba(248, 250, 255, 0.95))`,
  border: `2px solid ${BLUE_THEME.accent}20`,
  boxShadow: `0 25px 70px ${BLUE_THEME.primaryDark}25`,
  borderRadius: 4,
  position: "relative",
  overflow: "visible",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: `linear-gradient(90deg, transparent, ${BLUE_THEME.accent}, ${BLUE_THEME.primary}, transparent)`,
    borderRadius: "4px 4px 0 0",
  },
});

const StyledButton = styled(Button)({
  py: 2,
  px: 4,
  fontSize: "1.1rem",
  fontWeight: 800,
  borderRadius: 30,
  textTransform: "none",
  background: `linear-gradient(45deg, ${BLUE_THEME.primary} 0%, ${BLUE_THEME.primaryLight} 100%)`,
  boxShadow: `0 12px 40px ${BLUE_THEME.primary}50`,
  color: "white",
  height: 56,
  "&:hover": {
    background: `linear-gradient(45deg, ${BLUE_THEME.primaryLight} 0%, ${BLUE_THEME.primary} 100%)`,
    transform: "scale(1.05)",
    boxShadow: `0 20px 50px ${BLUE_THEME.primary}70`,
  },
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const AuthPage = ({ onSignInSuccess }) => {
  const [tabValue, setTabValue] = useState(0);
  const [signInData, setSignInData] = useState({ email: "", password: "", rememberMe: false });
  const [signUpData, setSignUpData] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!signInData.email || !signInData.password) {
      setError("Please fill in all fields");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(signInData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/signin.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: signInData.email,
          password: signInData.password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            signedIn: true,
            email: signInData.email,
            token: data.token,
            userId: data.userId,
            userName: data.full_name,
          })
        );
        setSuccess("Sign in successful!");
        if (onSignInSuccess) {
          setTimeout(() => onSignInSuccess(), 500);
        }
      } else {
        setError(data.message || "Sign in failed. Please check your credentials.");
      }
    } catch (err) {
      setError("Connection error. Please try again later.");
      console.error("Sign in error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!signUpData.fullName || !signUpData.email || !signUpData.phone || !signUpData.password || !signUpData.confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(signUpData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (signUpData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (signUpData.password !== signUpData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/signup.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: signUpData.fullName,
          email: signUpData.email,
          phone: signUpData.phone,
          password: signUpData.password,
          confirm_password: signUpData.confirmPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess("Account created successfully! Switching to sign in...");
        setTimeout(() => {
          setTabValue(0);
          setSignUpData({ fullName: "", email: "", phone: "", password: "", confirmPassword: "" });
        }, 1500);
      } else {
        setError(data.message || "Sign up failed. Please try again.");
      }
    } catch (err) {
      setError("Connection error. Please try again later.");
      console.error("Sign up error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: onSignInSuccess ? "auto" : "100vh",
        py: onSignInSuccess ? 0 : { xs: 8, md: 12 },
        background: onSignInSuccess ? "transparent" : `linear-gradient(180deg, ${BLUE_THEME.primaryDark}05, #f4f7ff)`,
        position: "relative",
        overflow: "hidden",
        "&::before": onSignInSuccess
          ? { display: "none" }
          : {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "300px",
              background: `linear-gradient(135deg, ${BLUE_THEME.accent}20 0%, ${BLUE_THEME.primary}10 100%)`,
              zIndex: 0,
            },
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1, p: onSignInSuccess ? 0 : undefined }}>
        {!onSignInSuccess && (
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h3"
              fontWeight={900}
              sx={{
                background: `linear-gradient(45deg, ${BLUE_THEME.primary}, ${BLUE_THEME.accent})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                mb: 2,
              }}
            >
              Velvizhi Construction
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: `${BLUE_THEME.primaryDark}99`,
                fontWeight: 500,
                maxWidth: 400,
                mx: "auto",
              }}
            >
              Sign in to your account or create a new one
            </Typography>
          </Box>
        )}

        {onSignInSuccess && (
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: BLUE_THEME.primary }}>
              Sign In / Sign Up
            </Typography>
          </Box>
        )}

        <StyledCard elevation={0}>
          <Box sx={{ borderBottom: 1, borderColor: `${BLUE_THEME.accent}20` }}>
            <Tabs
              value={tabValue}
              onChange={(e, val) => {
                setTabValue(val);
                setError("");
                setSuccess("");
              }}
              sx={{
                "& .MuiTabs-indicator": { backgroundColor: BLUE_THEME.accent, height: 3 },
                "& .MuiTab-root": {
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: `${BLUE_THEME.primaryDark}77`,
                  textTransform: "none",
                  "&.Mui-selected": { color: BLUE_THEME.primary },
                },
              }}
            >
              <Tab label="Sign In" />
              <Tab label="Sign Up" />
            </Tabs>
          </Box>

          <CardContent sx={{ p: { xs: 4, md: 5 } }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                {success}
              </Alert>
            )}

            {/* SIGN IN TAB */}
            <TabPanel value={tabValue} index={0}>
              <Box component="form" onSubmit={handleSignIn}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={signInData.email}
                  onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
                  margin="normal"
                  required
                  disabled={loading}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: "rgba(255,255,255,0.8)",
                      "& fieldset": { borderColor: `${BLUE_THEME.accent}30`, borderWidth: 2 },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined sx={{ color: BLUE_THEME.accent }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={signInData.password}
                  onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
                  margin="normal"
                  required
                  disabled={loading}
                  sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: 3, backgroundColor: "rgba(255,255,255,0.8)" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: BLUE_THEME.accent }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={loading} sx={{ color: BLUE_THEME.primaryDark }}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={signInData.rememberMe}
                      onChange={(e) => setSignInData({ ...signInData, rememberMe: e.target.checked })}
                      disabled={loading}
                      sx={{ color: `${BLUE_THEME.accent}50`, "&.Mui-checked": { color: BLUE_THEME.accent } }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: `${BLUE_THEME.primaryDark}cc`, fontWeight: 500 }}>Remember me</Typography>}
                  sx={{ mb: 4 }}
                />

                <StyledButton type="submit" fullWidth size="large" disabled={loading}>
                  {loading ? "Signing In..." : "Sign In"}
                </StyledButton>
              </Box>
            </TabPanel>

            {/* SIGN UP TAB */}
            <TabPanel value={tabValue} index={1}>
              <Box component="form" onSubmit={handleSignUp}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={signUpData.fullName}
                  onChange={(e) => setSignUpData({ ...signUpData, fullName: e.target.value })}
                  margin="normal"
                  required
                  disabled={loading}
                  sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: 3, backgroundColor: "rgba(255,255,255,0.8)" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon sx={{ color: BLUE_THEME.accent }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={signUpData.email}
                  onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
                  margin="normal"
                  required
                  disabled={loading}
                  sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: 3, backgroundColor: "rgba(255,255,255,0.8)" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined sx={{ color: BLUE_THEME.accent }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  value={signUpData.phone}
                  onChange={(e) => setSignUpData({ ...signUpData, phone: e.target.value })}
                  margin="normal"
                  required
                  disabled={loading}
                  sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: 3, backgroundColor: "rgba(255,255,255,0.8)" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlinedIcon sx={{ color: BLUE_THEME.accent }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={signUpData.password}
                  onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                  margin="normal"
                  required
                  disabled={loading}
                  helperText="Min. 6 characters"
                  sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: 3, backgroundColor: "rgba(255,255,255,0.8)" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: BLUE_THEME.accent }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={loading} sx={{ color: BLUE_THEME.primaryDark }}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={signUpData.confirmPassword}
                  onChange={(e) => setSignUpData({ ...signUpData, confirmPassword: e.target.value })}
                  margin="normal"
                  required
                  disabled={loading}
                  sx={{ mb: 4, "& .MuiOutlinedInput-root": { borderRadius: 3, backgroundColor: "rgba(255,255,255,0.8)" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: BLUE_THEME.accent }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" disabled={loading} sx={{ color: BLUE_THEME.primaryDark }}>
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledButton type="submit" fullWidth size="large" disabled={loading}>
                  {loading ? "Creating Account..." : "Create Account"}
                </StyledButton>
              </Box>
            </TabPanel>
          </CardContent>
        </StyledCard>
      </Container>
    </Box>
  );
};

export default AuthPage;
